'use client';
import React from 'react';

import MuiBox from '@new-components/atoms/MuiBox';
import MuiTypography from '@new-components/atoms/MuiTypography';
import MuiContainer from '@new-components/atoms/MuiContainer';
import {MediaQuery} from '@constants/theme';
import {VirtualTour} from '@new-components/molecules/VirtualTour';

export type WorldOfAldarCardProps = {
  title: string;
  description: string;
  virtualTourLink: string;
  stats?: StatsProps['stats'];
  labelColor?:string;
  /**
   * content to be shown on overlay when map is not interactive
   */
  children?: React.ReactNode;
};
export function WorldOfAldarCard(props: WorldOfAldarCardProps) {
  return (
    <>
      <MuiContainer sx={{marginBlock:{xs:'32px',md:'64px 96px'}}}>
        <MuiTypography
          sx={{
            fontWeight: { xs: 400, md: 300 },
            fontSize: { xs: '24px', md: '48px' },
            lineHeight: { xs: '30px', md: '60px' },
            letterSpacing: { xs: '-0.4px', md: '-0.8px' },
            color: props.labelColor ?? '#2d2d2d',
            textAlign: 'left',
            marginBottom: {xs:"12px",md:"24px"},
          }}>
          {props.title}
        </MuiTypography>
        <MuiTypography
          sx={{
            color: props.labelColor ?? '#2d2d2d',
            fontSize: {xs:'14px', md:'17px'},
            fontWeight: 400,
            lineHeight: '125%',
            letterSpacing: '-0.4px',
            textAlign: 'left',
            opacity:.6,
          }}>
          {props.description}
        </MuiTypography>
      </MuiContainer>
      <VirtualTour
        virtualTourLink={props.virtualTourLink}
        showOverlay={false}
        sx={{
          [MediaQuery.desktop]: {
            height: 'max(70vh, 580px)',
          },
        }}>
        {props.children}
      </VirtualTour>
      {props.stats && <StatisticsBlock stats={props.stats} />}
    </>
  );
}

type StatsProps = {
  stats?: {label: string; value: string; unit?: string}[];
};
function StatisticsBlock(props: StatsProps) {
  return (
    <MuiContainer
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        columnGap: '24px',
        rowGap: '12px',
        padding: '16px',
        [MediaQuery.desktop]: {
          gridTemplateColumns: `repeat(${props.stats?.length}, 1fr)`,
        },
      }}>
      {props.stats?.map(({label, value, unit}) => (
        <MuiBox
          key={`${value}-${label}`}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            boxSizing: 'border-box',
            padding: '12px',
            [MediaQuery.desktop]: {
              'borderRight': '1px solid #BABAC880',
              ':last-child': {
                borderRight: 'none',
              },
            },
          }}>
          <MuiBox
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
            }}>
            <MuiTypography
              sx={{
                fontSize: '32px',
                fontWeight: 300,
                lineHeight: '35.2px',
                letterSpacing: '-0.800000011920929px',
                textAlign: 'left',
                textUnderlinePosition: 'from-font',
                textDecorationSkipInk: 'none',
                color: '#2D2D2D',
              }}>
              {value}
            </MuiTypography>
            {unit && (
              <MuiTypography
                sx={{
                  fontSize: '22px',
                  fontWeight: 300,
                  lineHeight: '27.5px',
                  letterSpacing: '-0.4px',
                  textAlign: 'left',
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                }}>
                {unit}
              </MuiTypography>
            )}
          </MuiBox>
          <MuiTypography
            sx={{
              fontSize: '17px',
              fontWeight: 300,
              lineHeight: '23.8px',
              letterSpacing: '-0.4px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              color: '#2D2D2D',
              maxWidth: '100px',
            }}>
            {label}
          </MuiTypography>
        </MuiBox>
      ))}
    </MuiContainer>
  );
}
